import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Layers, Trash2, Plus, Save, Loader2, Check, X, Edit2, Copy, ArrowUp, ArrowDown, Star, MoreVertical, Download, Search, ArrowLeft } from "lucide-react";
import { resolveBackHref } from "@/lib/navTracker";
import { itemCostNZD, itemValueNZD, computeItemValuation } from "@/utils/itemValue";
import { fmt2 } from "@/lib/money";
import { useAuth, useTier } from "@/context/AuthContext";
import { listCollectionTypes, getCollectionType } from "@/lib/collectionTypes";
import { isSampleCollectionId } from "@/lib/sampleImport";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { useCollections } from "@/context/CollectionContext";
import EmptyState from "@/components/EmptyState";
import BottomNav from "@/components/BottomNav";
import ComboBox from "@/components/ComboBox";

// IS_PREVIEW — true when the app is running in the sandboxed Preview
// environment where browser downloads do NOT actually persist a file to
// the user's machine. Used ONLY to swap user-facing CSV messaging so we
// don't lie to the user with "Exported / Backup saved". In Full-stack /
// production deploys the download codepath is unchanged.
const IS_PREVIEW = typeof window !== "undefined" && /\bpreview\b/.test(window.location.hostname);

export default function Collections() {
  const navigate = useNavigate();
  const location = useLocation();
  // Context-aware Back — same resolver as ItemDetail / CollectionDetail.
  const backHref = resolveBackHref({
    stateFrom: location.state?.from,
    currentPath: location.pathname + location.search,
    fallback: "/",
  });
  const { collections, refresh, removeById } = useCollections();
  const { user } = useAuth();
  const { canCsvExport } = useTier();
  const [loading, setLoading] = useState(true);
  // Lightweight cache of every item across every collection, used ONLY to
  // compute per-collection aggregates (Cost / Value / Profit) for the new
  // sort options. Mirrors what Dashboard / CollectionDetail already do.
  // Fetched once on mount; refreshed when the collections list changes
  // (e.g. after a delete / create / rename / reorder).
  const [allItems, setAllItems] = useState([]);
  // 2026-02-26 — Portfolio-wide sales feed. Required so `aggByCollection`
  // can pass each item's sales history into `computeItemValuation` and
  // read the canonical `total_PL` (realised + unrealised, ex-GST). Same
  // pattern used by Analytics.jsx.
  const [allSales, setAllSales] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get("/items");
        if (!cancelled) setAllItems(Array.isArray(data) ? data : []);
      } catch { if (!cancelled) setAllItems([]); }
    })();
    return () => { cancelled = true; };
  }, [collections.length]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get("/sales");
        if (!cancelled) setAllSales(Array.isArray(data) ? data : []);
      } catch { if (!cancelled) setAllSales([]); }
    })();
    return () => { cancelled = true; };
  }, [collections.length]);

  // Browser-tab metadata for /collections only. `document.title` becomes
  // "Collections · CDI Collectables" and the `<meta name="description">`
  // gets a Collections-specific summary so bookmarks / social embeds /
  // recent-tab lists surface the correct context. Both are restored to
  // their index.html defaults on unmount so /collections/:id, /items,
  // /dashboard and the auth pages remain unaffected.
  useEffect(() => {
    const prevTitle = document.title;
    const metaEl = document.querySelector('meta[name="description"]');
    const prevDesc = metaEl ? metaEl.getAttribute("content") : null;
    document.title = "Collections · CDI Collectables";
    if (metaEl) {
      metaEl.setAttribute(
        "content",
        "Browse and manage your CDI Collectables collections — wine cellars, whisky vaults, spirits shelves and every other collectable tracked by cost, value and P/L in one place."
      );
    }
    return () => {
      document.title = prevTitle;
      if (metaEl && prevDesc !== null) metaEl.setAttribute("content", prevDesc);
    };
  }, []);
  // Map<collection_id, { cost, value, profit, totalPL, count, valuedItemsCount, collection_cost_value }> — derived once per render.
  const aggByCollection = useMemo(() => {
    // 2026-02-26 — Group sales by item_id once so every reducer below
    // reads O(1) per item. Same shape as Analytics.jsx `salesByItem`.
    const salesByItem = new Map();
    for (const s of allSales) {
      const k = s?.item_id;
      if (!k) continue;
      if (!salesByItem.has(k)) salesByItem.set(k, []);
      salesByItem.get(k).push(s);
    }
    const m = new Map();
    for (const c of collections) m.set(c.id, { cost: 0, value: 0, profit: 0, totalPL: 0, count: 0, valuedItemsCount: 0, collection_cost_value: 0 });
    for (const it of allItems) {
      const bucket = m.get(it.collection_id);
      if (!bucket) continue;
      const costNZD = Number(itemCostNZD(it)) || 0;
      bucket.cost  += costNZD;
      // True cost basis — every item contributes, regardless of valuation
      // status. Independent from `value` / `profit` so it can never be
      // masked by a missing valuation.
      bucket.collection_cost_value += costNZD;
      // Canonical inclusion gate — 2026-02-26 consolidation. Uses the
      // `includeInValuationTotals` flag from computeItemValuation instead
      // of the retired `itemValueNZD(it) === 0` proxy so every page shares
      // one source of truth for "does this item count towards value".
      const val = computeItemValuation(it, salesByItem.get(it.id) || []);
      if (val.includeInValuationTotals) {
        bucket.value += itemValueNZD(it);
        // 2026-02-26 — P/L now sourced from `total_PL` (realised +
        // unrealised, ex-GST) instead of the previous `value − cost`
        // shortcut. Matches the shared aggregators in
        // categoryAggregators.js and dashboardAggregators.js.
        bucket.totalPL += val.total_PL;
        bucket.valuedItemsCount += 1;
      }
      bucket.count += 1;
    }
    for (const v of m.values()) v.profit = v.totalPL;
    return m;
  }, [collections, allItems, allSales]);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  // Collection-type picker (Task A-Fix). Premium types were retired
  // 2026-02-26 — only the three Basic beverage types remain in the
  // registry so the dropdown surfaces them directly with no gating.
  const basicTypes = useMemo(() => listCollectionTypes({ plan: "basic" }), []);
  const [typeId, setTypeId] = useState(() => basicTypes[0]?.id || "wine");

  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  // Second-step modal — appears AFTER the CSV backup has downloaded and
  // BEFORE the destructive DELETE. Holds { id, name, filename } so we know
  // which collection to delete and which backup file just landed on disk.
  const [pendingDeleteAfterBackup, setPendingDeleteAfterBackup] = useState(null);
  // IDs that should be visually hidden (optimistic delete)
  const [hiddenIds, setHiddenIds] = useState(() => new Set());

  // Inline rename
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [savingRename, setSavingRename] = useState(false);

  // Duplicate state
  const [duplicatingId, setDuplicatingId] = useState(null);
  const duplicate = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    if (duplicatingId) return;
    setDuplicatingId(id);
    try {
      const { data } = await apiClient.post(`/collections/${id}/duplicate`);
      await refresh();
      toast.success(`Collection duplicated as "${data.name}".`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Duplicate failed.");
    } finally {
      setDuplicatingId(null);
    }
  };

  // Set-default state
  const [defaultingId, setDefaultingId] = useState(null);
  const setDefault = async (e, c) => {
    e.preventDefault();
    e.stopPropagation();
    if (defaultingId || c.is_default) return;
    setDefaultingId(c.id);
    try {
      await apiClient.post(`/collections/${c.id}/set-default`);
      await refresh();
      toast.success(`"${c.name}" set as default.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not set default.");
    } finally {
      setDefaultingId(null);
    }
  };

  const startRename = (e, c) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingId(c.id);
    setEditingName(c.name);
    setConfirmDeleteId(null);
  };
  const cancelRename = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingId(null);
    setEditingName("");
  };
  const saveRename = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    const trimmed = editingName.trim();
    if (!trimmed) { toast.error("Name is required."); return; }
    // Duplicate-name guard — case-insensitive, excludes the row being renamed.
    const clash = (collections || []).some(
      (c) => c.id !== id && (c.name || "").trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (clash) {
      toast.error("A collection with this name already exists. Please choose another name.");
      return;
    }
    setSavingRename(true);
    try {
      await apiClient.patch(`/collections/${id}`, { name: trimmed });
      await refresh();
      toast.success("Collection renamed.");
      setEditingId(null);
      setEditingName("");
    } catch {
      toast.error("Rename failed.");
    } finally {
      setSavingRename(false);
    }
  };

  useEffect(() => {
    (async () => { await refresh(); setLoading(false); })();
  }, [refresh]);

  // Kebab menu (collapses Edit / Copy / Delete)
  const [menuId, setMenuId] = useState(null);
  // Close the kebab menu on outside click / tap / Escape. Uses `pointerdown`
  // (unified mouse + touch) so mobile taps fire consistently. Hardened
  // against text-node targets that lack `.closest()`.
  useEffect(() => {
    if (!menuId) return;
    const isInsideMenu = (target) => {
      const el = target instanceof Element ? target : (target?.parentElement || null);
      if (!el) return false;
      return !!el.closest(
        `[data-testid="collection-menu-${menuId}"], [data-testid="collection-menu-toggle-${menuId}"]`
      );
    };
    const onDown = (e) => {
      if (!isInsideMenu(e.target)) setMenuId(null);
    };
    const onKey = (e) => { if (e.key === "Escape") setMenuId(null); };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuId]);

  const askDelete = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    setConfirmDeleteId(id);
  };

  const cancelDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setConfirmDeleteId(null);
  };

  // Exporting state (separate from delete-flow backup)
  const [exportingId, setExportingId] = useState(null);
  const exportCsv = async (e, collection) => {
    e.preventDefault();
    e.stopPropagation();
    if (exportingId) return;
    setExportingId(collection.id);
    try {
      await downloadBackupCsv({ ...collection }, "cdi-export");
    } catch {
      // toast already shown
    } finally {
      setExportingId(null);
    }
  };

  /**
   * Safety net before destructive delete: pull every item in this collection,
   * write them to a CSV file, and trigger a browser download. Resolves once
   * the download has been kicked off — caller waits ~1s then proceeds with
   * the actual DELETE so the browser has time to start writing the file.
   *
   * Schema matches the Items / CollectionDetail exporter so the backup is
   * directly re-importable later if needed.
   */
  const downloadBackupCsv = async (collection, prefix = "cdi-backup") => {
    // Preview-mode gate applies ONLY to the delete-time backup path.
    // Export CSV (prefix === "cdi-export") is uncoupled from delete safety
    // rules and must always succeed — including in Preview. The backup
    // path stays fail-closed so a delete never proceeds without a real CSV.
    if (IS_PREVIEW && prefix === "cdi-backup") {
      toast.error("CSV backup is disabled in Preview mode.");
      throw new Error("CSV backup disabled in Preview mode");
    }
    try {
      const { data: items } = await apiClient.get(`/items?collection_id=${collection.id}`);
      const list = items || [];
      const csvEscape = (v) => {
        const s = String(v ?? "");
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const num = (it, k) => {
        const d = it.item_data || {};
        const dyn = d[k === "Purchase Price" ? "Purchase Price" : "Estimated Value"];
        if (dyn !== undefined && dyn !== null && dyn !== "") return Number(dyn);
        return Number(it[k === "Purchase Price" ? "purchase_price" : "estimated_value"] || 0);
      };
      // Created At resolver — items may store the timestamp under several
      // keys depending on the creation path: the API currently serialises
      // it as `date_added`, while legacy / camelCase variants are also
      // possible. Pick the first non-null and format as
      // "YYYY-MM-DD HH:mm:ss" (UTC) so the column is always populated.
      const fmtCreatedAt = (it) => {
        const raw = it.date_added ?? it.created_at ?? it.createdAtUtc ?? it.createdAt;
        if (!raw) return "";
        const d = new Date(raw);
        if (isNaN(d.getTime())) return String(raw);
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getUTCFullYear()}-${pad(d.getUTCMonth()+1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
      };
      const cols = ["name","collection","currency","cost","est_value","created_at"];
      const lines = [cols.join(",")];
      for (const it of list) {
        const cost  = num(it, "Purchase Price");
        const value = num(it, "Estimated Value");
        const row = {
          name: (it.item_data && it.item_data["Item Name"]) || it.name || it.item_name || "Untitled",
          collection: collection.name,
          currency: it.currency_code || "NZD",
          cost: cost.toFixed(2),
          est_value: value.toFixed(2),
          created_at: fmtCreatedAt(it),
        };
        lines.push(cols.map((c) => csvEscape(row[c])).join(","));
      }
      const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0,10);
      const safe = collection.name.replace(/[^a-z0-9-_]+/gi, "-").toLowerCase();
      a.href = url;
      a.download = `${prefix}-${safe}-${today}.csv`;
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Give the browser a beat to commit the download before revoking.
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success(`${prefix === "cdi-export" ? "Exported" : "Backup saved"} → ${a.download} (${list.length} row${list.length === 1 ? "" : "s"})`);
      return list.length;
    } catch (e) {
      toast.error(`${prefix === "cdi-export" ? "Export" : "Backup"} failed${prefix === "cdi-backup" ? " — delete cancelled" : ""}.`);
      throw e;
    }
  };

  // Inline armed "Delete" click — just opens the confirmation modal. No
  // CSV, no DELETE yet. CSV + DELETE both happen only when the user
  // confirms inside the modal (see finalConfirmDelete).
  const confirmDelete = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    if (deletingId) return;
    const collection = collections.find((c) => c.id === id);
    if (!collection) return;
    setPendingDeleteAfterBackup({ id, name: collection.name });
  };

  // Called from the modal "Confirm Delete" button — runs the CSV backup
  // FIRST (fail-closed), then performs the actual DELETE. In Preview mode
  // downloadBackupCsv throws, which aborts the deletion entirely (per
  // spec: never delete without a real backup).
  const finalConfirmDelete = async () => {
    const ctx = pendingDeleteAfterBackup;
    if (!ctx) return;
    if (deletingId) return;
    const collection = collections.find((c) => c.id === ctx.id);
    if (!collection) return;
    setDeletingId(ctx.id);
    // Step 1 — CSV backup BEFORE the destructive call. Fail-closed.
    // Preview mode OR plan without canCsvExport: CSV cannot be
    // persisted, so the modal has already disclosed "no backup will
    // be created" — skip the CSV step entirely and proceed straight
    // to delete. Build mode + canCsvExport behaviour unchanged.
    if (!IS_PREVIEW && canCsvExport) {
      try {
        await downloadBackupCsv(collection);
      } catch {
        // Backup helper already toasted the failure; abort deletion.
        setDeletingId(null);
        return;
      }
      // Give the browser a beat to start writing the file before we fire
      // the destructive request.
      await new Promise((r) => setTimeout(r, 400));
    }
    // Step 2 — actual delete.
    setHiddenIds((prev) => {
      const next = new Set(prev);
      next.add(ctx.id);
      return next;
    });
    try {
      await apiClient.delete(`/collections/${ctx.id}`);
      toast.success("Collection deleted.");
      setConfirmDeleteId(null);
      setPendingDeleteAfterBackup(null);
      // Fix #9 Part 1 — deleting a collection ALSO clears every
      // internal Create-Collection form state so no cached Premium
      // selection or half-typed name leaks into the next attempt.
      setShowForm(false);
      setName("");
      setDescription("");
      setTypeId(basicTypes[0]?.id || "wine");
      // Instant, synchronous context-level removal so the tile
      // disappears from every consumer of useCollections() immediately
      // — no wait for the refetch round-trip.
      removeById(ctx.id);
      // Authoritative refetch (owner-scoped by CollectionContext) so
      // the SPA cache is guaranteed consistent with the backend after
      // deletion. If it fails, the optimistic removeById above still
      // leaves the UI correct until the next natural refresh.
      await refresh();
      // Housekeeping — the optimistic hide is no longer needed once
      // both the context and the authoritative refetch have settled.
      setHiddenIds(new Set());
    } catch (er) {
      const status = er?.response?.status;
      // Orphan reconciliation — the backend has no owner-scoped record
      // for this id (created before Fix 1 with a missing/mismatched
      // owner_id, or already deleted server-side). Silently strip the
      // stale tile from context — no success toast, no error toast —
      // and reconcile with the authoritative /collections list.
      // Legitimate failures (500, network, etc.) restore the tile and
      // surface the error as before.
      if (status === 404 || status === 403) {
        removeById(ctx.id);
        setConfirmDeleteId(null);
        setPendingDeleteAfterBackup(null);
        setShowForm(false);
        setName("");
        setDescription("");
        setTypeId(basicTypes[0]?.id || "wine");
        try { await refresh(); } catch { /* best-effort — context already clean */ }
        setHiddenIds(new Set());
      } else {
        setHiddenIds((prev) => {
          const next = new Set(prev);
          next.delete(ctx.id);
          return next;
        });
        toast.error(er?.response?.data?.detail || "Delete failed.");
      }
    } finally {
      setDeletingId(null);
    }
  };

  const create = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Collection name is required."); return; }
    // Task A-Fix-2: Collection Type is REQUIRED. Block save if no type is
    // selected (defensive — the dropdown defaults to "wine", but guard
    // against the user clearing it via dev tools / future UI changes).
    if (!typeId) {
      toast.error("Please select a collection type.");
      return;
    }
    // Client-side guard: refetch latest collections (don't trust stale local
    // state) then case-insensitively check for a duplicate name. Backend
    // also rejects with 409 as a final defence.
    setSaving(true);
    try {
      let latest = [];
      try {
        const { data } = await apiClient.get("/collections");
        latest = data || [];
      } catch {
        latest = collections || [];
      }
      const clash = latest.some(
        (c) => (c.name || "").trim().toLowerCase() === trimmed.toLowerCase()
      );
      if (clash) {
        toast.error("A collection with this name already exists. Please choose another name.");
        setSaving(false);
        return;
      }
      const { data } = await apiClient.post("/collections", {
        name: trimmed,
        description: description.trim(),
        type_id: typeId,           // Task A-Fix-2: REQUIRED — persisted in record
      });
      // Phase 3 monetisation: premium type + non-premium user was
      // previously "marked locked" in localStorage so the badge stuck
      // after the placeholder upgrade flow. Lock state is now derived
      // strictly from the user's tier (see line 1141 render + line 611
      // filter) — no localStorage write needed.
      toast.success("Collection created.");
      setName("");
      setDescription("");
      setTypeId(basicTypes[0]?.id || "wine");
      setShowForm(false);

      // === Task C: seed a default category with the registry's template
      // fields so the new collection arrives with a working schema instead
      // of forcing the user through manual category construction. Best-
      // effort: failures here surface a soft warning but do NOT roll back
      // the already-created collection. Existing collections are never
      // touched (this only runs on a fresh POST /collections response).
      let seededCatId = null;
      // Master-templated types (wine/whisky/spirits) are auto-seeded by
      // the backend on collection creation — the backend creates the
      // default category AND its master template fields inside the
      // POST /collections handler. Any client-side POST to
      // /categories or /custom_fields for these types is redundant and
      // triggers the misleading "default template could not be applied"
      // warning. Short-circuit here: mark seededCatId truthy so
      // navigation skips /setup and drops the user straight into the
      // already-seeded collection.
      const MASTER_TEMPLATE_TYPES = new Set(["wine", "whisky", "spirits"]);
      const isMasterTemplated = MASTER_TEMPLATE_TYPES.has(typeId);
      if (isMasterTemplated) {
        seededCatId = true;
      } else {
        try {
          const registryEntry = getCollectionType(typeId);
          const templates = registryEntry?.templateFields || [];
          // Fix #9 Parts 5 + 7 — Premium templates NEVER load. This
          // block is unreachable when the Premium gates in the dropdown
          // and `create()` are honoured, but defence in depth: bail
          // early with NO toast so a bypassed Premium POST never falls
          // through to the "template could not be applied" warning.
          const skipTemplateForPremium = registryEntry?.plan === "premium";
          if (skipTemplateForPremium) {
            // Do not attempt to seed a Premium template. Not an error.
          } else if (data?.id && registryEntry && templates.length > 0) {
            // Map registry field types → backend CategoryField field_type enum.
            // registry uses "text|number|date|select|multiselect|textarea";
            // backend accepts text/number/date/dropdown/multi_select/textarea
            // (plus currency/checkbox/image, none of which the registry uses).
            const TYPE_MAP = {
              text: "text",
              number: "number",
              date: "date",
              textarea: "textarea",
              select: "dropdown",
              multiselect: "multi_select",
            };
            // Use the registry name minus a trailing " Collection" suffix so
            // the category reads "Wine" / "Coins" / "Sneakers" as per spec
            // (e.g., "Wine Collection" → "Wine").
            const categoryName = (registryEntry.name || "").replace(/ Collection$/i, "").trim() || registryEntry.name;
            const { data: cat } = await apiClient.post("/categories", {
              category_name: categoryName,
              category_description: registryEntry.description || "",
              collection_id: data.id,
            });
            seededCatId = cat?.id || null;
            // POST each template field via /custom_fields — the endpoint
            // accepts writes on locked template types as user-defined
            // fields, and behaves identically to /fields for the rest
            // (adds master=false which is what we want for these
            // caller-supplied templates).
            for (let i = 0; i < templates.length; i++) {
              const f = templates[i];
              await apiClient.post(`/categories/${seededCatId}/custom_fields`, {
                field_name: f.label || f.id,
                field_type: TYPE_MAP[f.type] || "text",
                required: !!f.required,
                options: Array.isArray(f.options) ? f.options : [],
                placeholder: "",
                order: i,
              });
            }
          }
        } catch (seedErr) {
          // Non-blocking — the collection itself exists. User can still add
          // categories / fields manually.
          toast("Collection created, but the default template could not be applied. You can add fields manually.", { icon: "⚠️" });
        }
      }

      await refresh();
      // Onboarding redirect: drop the user straight into the "Create your
      // first category" screen instead of an empty collection. Removes the
      // dead-end UX of landing on a zero-category CollectionDetail.
      // Task C: if we successfully seeded a category, skip /setup and go
      // straight to the collection — they already have a working schema.
      if (data?.id) {
        navigate(seededCatId ? `/collections/${data.id}` : `/collections/${data.id}/setup`);
      }
    } catch (e) {
      const raw = e?.response?.data?.detail;
      const msg = Array.isArray(raw)
        ? raw.map((er) => `${(er.loc || []).slice(-1)[0] || "field"}: ${er.msg}`).join("; ")
        : (typeof raw === "string" ? raw : "Failed to create collection.");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // CDI Master Rule: Trial + Basic previously hid Premium-only
  // collections. The Premium type registry was retired 2026-02-26,
  // so `visibleCollections` now only strips optimistically-hidden
  // rows (mid-delete). Every remaining collection is visible.
  const visibleCollections = collections.filter((c) => !hiddenIds.has(c.id));

  const cancelFinalConfirm = () => {
    setPendingDeleteAfterBackup(null);
    // Reset the inline "click again to confirm" state so the very next delete
    // attempt re-triggers the full flow from scratch (per spec).
    setConfirmDeleteId(null);
    toast("Delete cancelled.", { icon: "🛡️" });
  };

  // ---- Sorting --------------------------------------------------------
  const [sortMode, setSortMode] = useState(() => {
    try { return localStorage.getItem("cdi.collections.sort") || "custom"; }
    catch { return "custom"; }
  });
  useEffect(() => {
    try { localStorage.setItem("cdi.collections.sort", sortMode); } catch (e) { /* noop */ }
  }, [sortMode]);

  // ---- Search + Autosuggest ------------------------------------------
  // Live free-text search across Collection name and description. Matches
  // case-insensitive substrings. Drives BOTH (a) an autosuggest dropdown
  // directly under the input — clicking a suggestion navigates to that
  // collection — and (b) the underlying visible grid below. Mirrors the
  // Items page search styling (leading magnifier icon + strong border).
  const [searchInput, setSearchInput] = useState("");
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [activeSuggestIdx, setActiveSuggestIdx] = useState(-1);
  const searchWrapRef = useRef(null);
  // Close the suggestion dropdown on outside click / Escape.
  useEffect(() => {
    if (!suggestOpen) return;
    const onDown = (e) => {
      if (!searchWrapRef.current) return;
      if (!searchWrapRef.current.contains(e.target)) setSuggestOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setSuggestOpen(false); };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [suggestOpen]);

  const matchSearch = (c, q) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return (
      String(c.name || "").toLowerCase().includes(needle) ||
      String(c.description || "").toLowerCase().includes(needle)
    );
  };
  // Filtered list used ONLY by the autosuggest dropdown. The grid below
  // intentionally renders the FULL (unfiltered) `visibleCollections` so the
  // filtered result never appears in two places on screen.
  const searchMatches = (collections || []).filter((c) => matchSearch(c, searchInput));
  const suggestions = searchInput.trim() ? searchMatches.slice(0, 10) : [];

  const openSuggestion = (id) => {
    setSuggestOpen(false);
    setSearchInput("");
    navigate(`/collections/${id}`);
  };

  // Single source of truth for the visible grid: `visibleCollections`,
  // sorted by `sortMode`. Search no longer mutates this list — the
  // autosuggest dropdown is the only surface that shows filtered results.
  // Persisted "alpha" / "newest" values stay valid via aliases below so
  // upgraded users don't lose their saved preference.
  const sortedCollections = [...visibleCollections].sort((a, b) => {
    const mode = sortMode === "alpha" ? "name_asc" : sortMode;
    if (mode === "name_asc")  return (a.name || "").localeCompare(b.name || "");
    if (mode === "name_desc") return (b.name || "").localeCompare(a.name || "");
    if (mode === "newest")    return (b.created_at || "").localeCompare(a.created_at || "");
    if (mode === "cost_asc" || mode === "cost_desc" ||
        mode === "value_asc" || mode === "value_desc" ||
        mode === "profit_asc" || mode === "profit_desc") {
      const aAg = aggByCollection.get(a.id) || { cost: 0, value: 0, profit: 0 };
      const bAg = aggByCollection.get(b.id) || { cost: 0, value: 0, profit: 0 };
      const key = mode.startsWith("cost")   ? "cost"
                : mode.startsWith("value")  ? "value"
                : "profit";
      const diff = aAg[key] - bAg[key];
      return mode.endsWith("_desc") ? -diff : diff;
    }
    // "custom" — sort_order ascending, fall back to created_at for ties
    const ao = a.sort_order ?? 0;
    const bo = b.sort_order ?? 0;
    if (ao !== bo) return ao - bo;
    return (a.created_at || "").localeCompare(b.created_at || "");
  });

  // Move a row up/down in custom mode and persist the new order.
  const [reordering, setReordering] = useState(false);
  const moveRow = async (id, dir) => {
    if (reordering) return;
    const ids = sortedCollections.map((c) => c.id);
    const idx = ids.indexOf(id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= ids.length) return;
    [ids[idx], ids[target]] = [ids[target], ids[idx]];
    setReordering(true);
    try {
      await apiClient.put("/collections/order", { order: ids });
      await refresh();
    } catch {
      toast.error("Reorder failed.");
    } finally {
      setReordering(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="collections-page">
      <button
        type="button"
        onClick={() => navigate(backHref)}
        data-testid="collections-back"
        aria-label="Back to previous screen"
        className="vp-btn vp-btn-secondary vp-btn-sm mb-4 inline-flex items-center gap-2"
      >
        <ArrowLeft size={14} />
        <span>Back</span>
      </button>
      {pendingDeleteAfterBackup && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="cols-modal-title"
          data-testid="cols-delete-modal"
          onClick={(e) => {
            // Close on backdrop click (when target is the wrapper itself).
            if (e.target === e.currentTarget && deletingId !== pendingDeleteAfterBackup.id) {
              cancelFinalConfirm();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape" && deletingId !== pendingDeleteAfterBackup.id) {
              cancelFinalConfirm();
            }
          }}
          tabIndex={-1}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm vp-fade-in p-4"
        >
          <div
            className="vp-card max-w-md w-full p-6 shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={cancelFinalConfirm}
              disabled={deletingId === pendingDeleteAfterBackup.id}
              aria-label="Close"
              data-testid="cols-modal-close"
              className="absolute top-3 right-3 p-1 rounded hover:bg-[hsl(var(--muted))] vp-muted disabled:opacity-40"
            >
              <X size={16} />
            </button>
            <h2 id="cols-modal-title" className="text-base font-semibold mb-2">
              Delete collection?
            </h2>
            <p className="text-sm vp-muted mb-5">
              {!canCsvExport ? (
                <>
                  <span>CSV backup is disabled for your plan. You can still delete <span className="font-medium text-[hsl(var(--fg))]">{pendingDeleteAfterBackup.name}</span>, but no backup will be created.</span>
                </>
              ) : IS_PREVIEW ? (
                <>
                  <span>CSV backup is disabled in Preview mode. You can still delete <span className="font-medium text-[hsl(var(--fg))]">{pendingDeleteAfterBackup.name}</span>, but no backup will be created.</span>
                </>
              ) : (
                <>
                  Deleting <span className="font-medium text-[hsl(var(--fg))]">{pendingDeleteAfterBackup.name}</span> will also remove every item and category inside it. This action cannot be undone from the UI. A CSV backup will be downloaded automatically before the delete runs.
                </>
              )}
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={cancelFinalConfirm}
                disabled={deletingId === pendingDeleteAfterBackup.id}
                data-testid="cols-modal-cancel"
                className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={finalConfirmDelete}
                disabled={deletingId === pendingDeleteAfterBackup.id}
                data-testid="cols-modal-confirm"
                className="vp-btn vp-btn-sm bg-[hsl(var(--loss))] text-white hover:opacity-90 disabled:opacity-50"
              >
                {deletingId === pendingDeleteAfterBackup.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                <span className="text-xs font-medium">{IS_PREVIEW || !canCsvExport ? "Delete without backup" : "Confirm Delete"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="vp-label text-black dark:text-white mb-2">Collections</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-black dark:text-white">
            {loading ? "…" : `${visibleCollections.length} collection${visibleCollections.length === 1 ? "" : "s"}`}
          </h1>
        </div>
        <button
          type="button"
          onClick={() => {
            // Fix #9 Part 1 — reset ALL form state on every open so
            // no stale Premium selection (or cached name / description)
            // leaks into a fresh Basic collection.
            if (showForm) {
              setShowForm(false);
              return;
            }
            setName("");
            setDescription("");
            setTypeId(basicTypes[0]?.id || "wine");
            setShowForm(true);
          }}
          data-testid="collections-create"
          className="vp-btn vp-btn-primary"
        >
          <Plus size={16} /> Create Collection
        </button>
      </div>

      {/* Search bar with autosuggest — filters collections live by name OR
          description as the user types. Clicking a suggestion in the
          dropdown opens that collection. Mirrors the Items page search
          styling (leading magnifier icon + stronger border). */}
      <div className="mb-4 relative" ref={searchWrapRef} data-testid="collections-search">
        <Search
          size={16}
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 z-10 text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]"
        />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            setSuggestOpen(true);
            setActiveSuggestIdx(-1);
          }}
          onFocus={() => { if (searchInput.trim()) setSuggestOpen(true); }}
          onKeyDown={(e) => {
            if (!suggestOpen || suggestions.length === 0) {
              if (e.key === "Enter" && searchInput.trim() && suggestions.length === 1) {
                e.preventDefault();
                openSuggestion(suggestions[0].id);
              }
              return;
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActiveSuggestIdx((i) => Math.min(suggestions.length - 1, i + 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActiveSuggestIdx((i) => Math.max(-1, i - 1));
            } else if (e.key === "Enter") {
              e.preventDefault();
              const pick = activeSuggestIdx >= 0 ? suggestions[activeSuggestIdx] : suggestions[0];
              if (pick) openSuggestion(pick.id);
            }
          }}
          placeholder="Search collections…"
          data-testid="collections-search-input"
          aria-autocomplete="list"
          aria-controls="collections-suggest-list"
          aria-expanded={suggestOpen && suggestions.length > 0}
          className="vp-input w-full !pl-9 !border-[hsl(var(--border-strong))]"
        />
        {searchInput && (
          <button
            type="button"
            onClick={() => { setSearchInput(""); setSuggestOpen(false); }}
            data-testid="collections-search-clear"
            aria-label="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 z-10 p-1 rounded hover:bg-[hsl(var(--muted))] vp-muted"
          >
            <X size={14} />
          </button>
        )}
        {suggestOpen && suggestions.length > 0 && (
          <ul
            id="collections-suggest-list"
            role="listbox"
            data-testid="collections-suggest-list"
            className="absolute left-0 right-0 top-full mt-1 z-30 max-h-72 overflow-auto rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elevated))] shadow-lg py-1 vp-fade-in"
          >
            {suggestions.map((c, i) => (
              <li
                key={c.id}
                role="option"
                aria-selected={i === activeSuggestIdx}
                data-testid={`collections-suggest-${c.id}`}
                onMouseDown={(e) => { e.preventDefault(); openSuggestion(c.id); }}
                onMouseEnter={() => setActiveSuggestIdx(i)}
                className={`px-3 py-2 cursor-pointer flex items-center gap-3 ${
                  i === activeSuggestIdx ? "bg-[hsl(var(--muted))]" : "hover:bg-[hsl(var(--muted))]"
                }`}
              >
                <div className="w-7 h-7 rounded bg-[hsl(var(--accent))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))] flex items-center justify-center shrink-0">
                  <Layers size={13} strokeWidth={1.75} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  {c.description && (
                    <div className="text-xs vp-muted truncate">{c.description}</div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showForm && (
        <section className="vp-card p-6 sm:p-7 mb-8 vp-fade-in" data-testid="collections-form">
          <h2 className="text-lg font-semibold mb-1">New Collection</h2>
          <p className="text-xs vp-muted mb-5">Give it a name and a short description.</p>
          <div className="space-y-4 mb-5">
            <div>
              <label htmlFor="col-new-name" className="block text-xs font-medium mb-1.5 vp-muted">
                Collection Name <span className="text-[hsl(var(--loss))]">*</span>
              </label>
              <input
                id="col-new-name"
                data-testid="collections-form-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. My Wine Cellar"
                className="vp-input"
              />
            </div>
            <div>
              <label htmlFor="col-new-type" className="block text-xs font-medium mb-1.5 vp-muted">
                Select Collection Type <span className="text-[hsl(var(--loss))]">*</span>
              </label>
              <select
                id="col-new-type"
                data-testid="collections-form-type"
                value={typeId}
                onChange={(e) => setTypeId(e.target.value)}
                className="vp-input"
              >
                <optgroup label="Basic">
                  {basicTypes.map((t) => (
                    <option key={t.id} value={t.id} data-testid={`collections-form-type-opt-${t.id}`}>
                      {t.name}
                    </option>
                  ))}
                </optgroup>
              </select>
              {typeId && (
                <p className="text-[11px] vp-muted mt-1.5" data-testid="collections-form-type-desc">
                  <span>{basicTypes.find((t) => t.id === typeId)?.description}</span>
                </p>
              )}
            </div>
            <div>
              <label htmlFor="col-new-desc" className="block text-xs font-medium mb-1.5 vp-muted">Description</label>
              <input
                id="col-new-desc"
                data-testid="collections-form-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="A short description (optional)"
                className="vp-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setShowForm(false); setName(""); setDescription(""); setTypeId(basicTypes[0]?.id || "wine"); }}
              className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={create}
              disabled={saving || !name.trim() || !typeId}
              data-testid="collections-form-save"
              className="vp-btn vp-btn-primary vp-btn-sm disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Saving…" : "Save Collection"}
            </button>
          </div>
        </section>
      )}

      {/* Sort selector */}
      {visibleCollections.length > 1 && (
        <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="collections-sort">
          <span className="text-xs text-black dark:text-white">Sort</span>
          <div className="w-64 sm:w-72">
            <ComboBox
              id="collections-sort-select"
              testid="collections-sort-select"
              value={sortMode}
              onChange={(v) => setSortMode(v || "custom")}
              allowAdd={false}
              options={[
                { value: "custom",       label: "Custom order" },
                { value: "name_asc",     label: "Name (A → Z)" },
                { value: "name_desc",    label: "Name (Z → A)" },
                { value: "newest",       label: "Newest first" },
                { value: "cost_desc",    label: "Purchase Price (high → low)" },
                { value: "cost_asc",     label: "Purchase Price (low → high)" },
                { value: "value_desc",   label: "Est. Value (high → low)" },
                { value: "value_asc",    label: "Est. Value (low → high)" },
                { value: "profit_desc",  label: "Profit (high → low)" },
                { value: "profit_asc",   label: "Profit (low → high)" },
              ]}
            />
          </div>
          {sortMode === "custom" && (
            <span className="text-xs vp-muted hidden sm:inline">Use ↑↓ on each tile to rearrange</span>
          )}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => <div key={i} className="vp-card p-6 h-28 vp-skeleton" />)}
        </div>
      ) : visibleCollections.length === 0 ? (
        <EmptyState
          Icon={Layers}
          title="No collections yet"
          body="Tap Create Collection above to add your first one."
          testid="collections-empty"
        />
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 landscape:grid-cols-2 lg:landscape:grid-cols-3 auto-rows-fr gap-4 vp-stagger" data-testid="collections-list">
          {sortedCollections.map((c, idx) => (
            <li key={c.id} className="relative min-h-[7rem] max-w-full" style={{ zIndex: menuId === c.id ? 30 : undefined }}>
              {editingId === c.id ? (
                <div
                  className="vp-card p-5 flex items-center gap-3"
                  data-testid={`collection-rename-form-${c.id}`}
                >
                  <div className="w-11 h-11 rounded-lg bg-[hsl(var(--accent))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))] flex items-center justify-center shrink-0">
                    <Layers size={18} strokeWidth={1.75} />
                  </div>
                  <input
                    type="text"
                    autoFocus
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveRename(e, c.id);
                      else if (e.key === "Escape") cancelRename(e);
                    }}
                    data-testid={`collection-rename-input-${c.id}`}
                    className="vp-input flex-1 min-w-0 h-9"
                    placeholder="Collection name"
                  />
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={(e) => saveRename(e, c.id)}
                      disabled={savingRename || !editingName.trim()}
                      data-testid={`collection-rename-save-${c.id}`}
                      aria-label="Save rename"
                      className="vp-btn vp-btn-sm !h-8 !w-8 !p-0 bg-[hsl(var(--gold))] text-[#1a1a1a] hover:opacity-90 disabled:opacity-50"
                    >
                      {savingRename ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    </button>
                    <button
                      type="button"
                      onClick={cancelRename}
                      data-testid={`collection-rename-cancel-${c.id}`}
                      aria-label="Cancel rename"
                      className="vp-btn vp-btn-ghost vp-btn-sm !h-8 !w-8 !p-0 border border-[hsl(var(--border))]"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              ) : (
                <Link
                  to={`/collections/${c.id}`}
                  data-testid={`collection-tile-${c.id}`}
                  className="vp-card vp-card-hover p-5 group flex items-center gap-4 min-h-[5.25rem] h-full"
                >
                  <div className="w-11 h-11 rounded-lg bg-[hsl(var(--accent))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))] flex items-center justify-center shrink-0 relative">
                    <Layers size={18} strokeWidth={1.75} />
                    {isSampleCollectionId(c.id) && (
                      <span
                        data-testid={`collection-tile-sample-${c.id}`}
                        title="Sample data — safe to delete"
                        aria-label="Sample cellar"
                        className="absolute -bottom-1 -right-1 px-1.5 h-4 rounded-full bg-[hsl(var(--gold))] text-[#1a1a1a] text-[9px] font-bold tracking-wider uppercase flex items-center border border-[hsl(var(--bg))] shadow"
                      >
                        Sample
                      </span>
                    )}
                  </div>
                  {/* Title column — wraps name to 2 lines and shows a
                      secondary metadata line ("N items • Type") underneath.
                      The right-pad reserve (pr-20 / pr-28 when reorder
                      arrows are showing) is on the column so both lines
                      stay clear of the absolute-positioned action cluster. */}
                  <div className={`flex-1 min-w-0 flex flex-col justify-center ${sortMode === "custom" ? "pr-28" : "pr-20"}`}>
                    <span data-testid={`collection-tile-name-${c.id}`} className="text-base font-medium tracking-tight leading-snug line-clamp-2 break-words">
                      {c.name}
                    </span>
                    <span data-testid={`collection-tile-meta-${c.id}`} className="text-xs vp-muted mt-1 break-words">
                      {(aggByCollection.get(c.id)?.count ?? 0)} item{(aggByCollection.get(c.id)?.count ?? 0) === 1 ? "" : "s"}
                      {getCollectionType(c.type_id)?.name ? ` • ${getCollectionType(c.type_id).name}` : ""}
                    </span>
                    {(() => {
                      const bucket = aggByCollection.get(c.id);
                      const valued = bucket?.valuedItemsCount ?? 0;
                      const profit = bucket?.profit ?? 0;
                      if (valued === 0) {
                        return (
                          <span
                            data-testid={`collection-tile-profit-${c.id}`}
                            className="text-xs vp-muted mt-0.5 italic break-words"
                            title="No items have a valuation set. Profit/Loss will appear once at least one item has a market value."
                          >
                            Awaiting Valuation
                          </span>
                        );
                      }
                      const positive = profit >= 0;
                      return (
                        <span
                          data-testid={`collection-tile-profit-${c.id}`}
                          className={`text-xs mt-0.5 tabular-nums break-words ${
                            profit > 0
                              ? "text-[hsl(var(--gain))]"
                              : profit < 0
                                ? "text-[hsl(var(--loss))]"
                                : "vp-muted"
                          }`}
                          title="Profit (Value − Cost) — Items without a valuation are excluded from Market Value and Profit calculations but still contribute to Total Cost."
                        >
                          {positive ? "+" : "−"}${fmt2(Math.abs(profit))} P/L
                        </span>
                      );
                    })()}
                    {/* Optional description subtitle — rendered only when
                        the collection has a non-empty description. Sits as
                        a third line under the metadata. Wraps to 2 lines
                        max and truncates with ellipsis after that so very
                        long descriptions never push the card layout. */}
                    {(c.description || "").trim() && (
                      <span
                        data-testid={`collection-tile-description-${c.id}`}
                        className="text-xs vp-muted mt-1 leading-snug line-clamp-2 break-words"
                      >
                        {c.description}
                      </span>
                    )}
                  </div>
                </Link>
              )}
              {/* Action buttons: only show when not in edit mode */}
              {editingId !== c.id && (confirmDeleteId === c.id ? (
                <div
                  className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 bg-[hsl(var(--bg-elevated))] border border-[hsl(var(--border))] rounded-md p-1 shadow-sm"
                  data-testid={`collection-delete-confirm-${c.id}`}
                >
                  <button
                    type="button"
                    onClick={(e) => confirmDelete(e, c.id)}
                    disabled={deletingId === c.id}
                    data-testid={`collection-delete-yes-${c.id}`}
                    aria-label={`Confirm delete ${c.name}`}
                    className="vp-btn vp-btn-sm !h-8 !px-2 bg-[hsl(var(--loss))] text-white hover:opacity-90 disabled:opacity-50"
                  >
                    {deletingId === c.id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    <span className="text-xs font-medium">Delete</span>
                  </button>
                  <button
                    type="button"
                    onClick={cancelDelete}
                    data-testid={`collection-delete-cancel-${c.id}`}
                    aria-label="Cancel delete"
                    className="vp-btn vp-btn-ghost vp-btn-sm !h-8 !w-8 !p-0 border border-[hsl(var(--border))]"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                  {sortMode === "custom" && (
                    <div className="flex flex-col gap-0.5 mr-1" data-testid={`collection-reorder-${c.id}`}>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); moveRow(c.id, -1); }}
                        disabled={idx === 0 || reordering}
                        data-testid={`collection-up-${c.id}`}
                        aria-label="Move up"
                        title="Move up"
                        className="vp-btn vp-btn-ghost !h-5 !w-7 !p-0 border border-[hsl(var(--border))] bg-[hsl(var(--bg-elevated))] hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))] disabled:opacity-30"
                      >
                        <ArrowUp size={11} />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); moveRow(c.id, 1); }}
                        disabled={idx === sortedCollections.length - 1 || reordering}
                        data-testid={`collection-down-${c.id}`}
                        aria-label="Move down"
                        title="Move down"
                        className="vp-btn vp-btn-ghost !h-5 !w-7 !p-0 border border-[hsl(var(--border))] bg-[hsl(var(--bg-elevated))] hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))] disabled:opacity-30"
                      >
                        <ArrowDown size={11} />
                      </button>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={(e) => setDefault(e, c)}
                    disabled={defaultingId === c.id}
                    data-testid={`collection-default-${c.id}`}
                    aria-label={c.is_default ? `${c.name} is the default` : `Make ${c.name} default`}
                    title={c.is_default ? "Default collection (used for new items)" : "Set as default collection"}
                    className={`vp-btn vp-btn-ghost vp-btn-sm !h-9 !w-9 !p-0 border bg-[hsl(var(--bg-elevated))] disabled:opacity-50 ${
                      c.is_default
                        ? "border-[hsl(var(--gold))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]"
                        : "border-[hsl(var(--border))] hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))]"
                    }`}
                  >
                    {defaultingId === c.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Star size={14} fill={c.is_default ? "currentColor" : "none"} />
                    )}
                  </button>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setMenuId((prev) => (prev === c.id ? null : c.id));
                        setConfirmDeleteId(null);
                      }}
                      data-testid={`collection-menu-toggle-${c.id}`}
                      aria-label={`More actions for ${c.name}`}
                      aria-haspopup="menu"
                      aria-expanded={menuId === c.id}
                      title="More actions"
                      className="vp-btn vp-btn-ghost vp-btn-sm !h-9 !w-9 !p-0 border border-[hsl(var(--border))] bg-[hsl(var(--bg-elevated))] hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))]"
                    >
                      <MoreVertical size={14} />
                    </button>
                    {menuId === c.id && (
                      <div
                        role="menu"
                        data-testid={`collection-menu-${c.id}`}
                        className="absolute right-0 top-full mt-1 z-20 min-w-[140px] rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elevated))] shadow-lg py-1 vp-fade-in"
                      >
                        <button
                          type="button"
                          role="menuitem"
                          onClick={(e) => { setMenuId(null); startRename(e, c); }}
                          data-testid={`collection-rename-${c.id}`}
                          className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-[hsl(var(--muted))]"
                        >
                          <Edit2 size={13} /> Rename
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={(e) => { setMenuId(null); duplicate(e, c.id); }}
                          disabled={duplicatingId === c.id}
                          data-testid={`collection-duplicate-${c.id}`}
                          className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-[hsl(var(--muted))] disabled:opacity-50"
                        >
                          {duplicatingId === c.id ? <Loader2 size={13} className="animate-spin" /> : <Copy size={13} />} Duplicate
                        </button>
                        {canCsvExport && (
                          <button
                            type="button"
                            role="menuitem"
                            onClick={(e) => { setMenuId(null); exportCsv(e, c); }}
                            disabled={exportingId === c.id}
                            data-testid={`collection-export-csv-${c.id}`}
                            className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-[hsl(var(--muted))] disabled:opacity-50"
                          >
                            {exportingId === c.id ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Export CSV
                          </button>
                        )}
                        <button
                          type="button"
                          role="menuitem"
                          onClick={(e) => { setMenuId(null); askDelete(e, c.id); }}
                          data-testid={`collection-delete-${c.id}`}
                          className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 text-[hsl(var(--loss))] hover:bg-[hsl(var(--muted))]"
                        >
                          <Trash2 size={13} /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </li>
          ))}
        </ul>
      )}

      <BottomNav current="collections" />
    </div>
  );
}
